// Gmail draft path — the per-user "draft into the connected Gmail account"
// action that sits alongside `useProviderDraft` (Outlook). It POSTs a
// recipient-optional draft to `/api/gmail/draft`, then toasts and opens the
// returned Gmail deep link for the IM to review + send.
//
// Page 13: the demo connects a real `demo@septima.ai` Gmail via the existing
// Google OAuth dance (`GET /api/gmail/auth`); an approved EMISSARY draft then
// pushes into that mailbox's Drafts. The draft endpoint creates a draft only —
// it never sends — mirroring the Outlook advisory-draft path.
//
// One hook instance owns one in-flight draft (`busy`), so render a separate
// instance per independent draft affordance.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/lib/useToast';
import type { ProviderDraftInput } from './use-provider-draft';

export interface GmailStatus {
    /** Server has GOOGLE_CLIENT_ID / _SECRET / _REDIRECT_URI set. */
    configured: boolean;
    /** A `(userId, 'google')` oauth_accounts row with a refresh token exists. */
    connected: boolean;
    /** The connected Gmail address (e.g. demo@septima.ai), if known. */
    email: string | null;
    connectedAt?: string | null;
}

const GMAIL_STATUS_KEY = ['integration', 'gmail', 'status'] as const;

/**
 * Connection status of the signed-in user's Gmail. Gates the draft affordance:
 * when not connected the button becomes a "Connect Gmail" link to the OAuth
 * start route; when connected it pushes a draft.
 */
export function useGmailStatus() {
    return useQuery<GmailStatus>({
        queryKey: GMAIL_STATUS_KEY,
        queryFn: async () => {
            const res = await apiFetch('/api/gmail/status', { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return (await res.json()) as GmailStatus;
        },
        staleTime: 30_000,
    });
}

/**
 * Draft a client email into the connected Gmail account (demo@septima.ai for
 * the demo). Returns the draft id + a Gmail deep link so the caller can open it
 * for review + sending.
 */
export function useGmailDraft() {
    const { pushToast } = useToast();
    const [busy, setBusy] = useState(false);

    const draft = async (input: ProviderDraftInput): Promise<void> => {
        setBusy(true);
        try {
            const res = await apiFetch('/api/gmail/draft', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    to: input.to ?? '',
                    subject: input.subject,
                    body: input.body,
                }),
            });
            const data = (await res.json().catch(() => ({}))) as {
                ok?: boolean;
                webUrl?: string;
                error?: string;
                message?: string;
            };
            if (!res.ok || !data.ok) {
                throw new Error(data.error ?? data.message ?? `Draft failed (${res.status})`);
            }
            pushToast({ kind: 'success', message: 'Draft created in Gmail — opening it now' });
            if (data.webUrl) window.open(data.webUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            pushToast({
                kind: 'error',
                message: err instanceof Error ? err.message : 'Could not create the Gmail draft',
            });
        } finally {
            setBusy(false);
        }
    };

    return { draft, busy };
}

/** Start the Google OAuth consent dance to connect a Gmail mailbox. */
export function startGmailConnect(): void {
    window.location.assign('/api/gmail/auth');
}
